import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
} from "../services/adminUsersService";
import { ROLES } from "../utils/permissions";
import { useAuth } from "../context/AuthContext";

const emptyForm = {
  id: null,
  name: "",
  email: "",
  mobile: "",
  password: "",
  role: ROLES.ADMIN,
  status: 1,
};

const roleBadge = (role, label) => {
  const map = {
    [ROLES.SUPER_ADMIN]: { bg: "#1e293b", color: "#fff" },
    [ROLES.ADMIN]: { bg: "#16632b", color: "#fff" },
    [ROLES.SALES]: { bg: "#3b82f6", color: "#fff" },
  };
  const m = map[role] || { bg: "#64748b", color: "#fff" };
  return (
    <span
      className="px-10 py-2 rounded-pill text-xs fw-semibold"
      style={{ background: m.bg, color: m.color }}
    >
      {label || role}
    </span>
  );
};

const AdminUsersLayer = () => {
  const { hasPermission, isSuperAdmin, user: me, roles, roleLabel } = useAuth();
  const canManage = hasPermission("admin_users.manage");
  // Roles that can be assigned via this form:
  // - everything except super_admin, unless the current user is super_admin.
  const assignableRoles = roles.filter((r) =>
    r.role_key === ROLES.SUPER_ADMIN ? isSuperAdmin : true
  );

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminUsers(search);
      if (res?.status) setRows(res.data || []);
      else toast.error(res?.msg || "Failed to load users");
    } catch {
      toast.error("Failed to load users");
    }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name || "",
      email: row.email || "",
      mobile: row.mobile || "",
      password: "",
      role: row.role,
      status: Number(row.status ?? 1),
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canManage) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const res = form.id
        ? await updateAdminUser(form.id, payload)
        : await createAdminUser(payload);
      if (res?.status) {
        toast.success(res.msg || "Saved");
        setShowForm(false);
        setForm(emptyForm);
        fetchRows();
      } else {
        toast.error(res?.msg || "Save failed");
      }
    } catch {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const handleDelete = async (row) => {
    if (!isSuperAdmin) return;
    if (!window.confirm(`Delete admin user "${row.name}"?`)) return;
    try {
      const res = await deleteAdminUser(row.id);
      if (res?.status) {
        toast.success(res.msg);
        fetchRows();
      } else {
        toast.error(res?.msg || "Delete failed");
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <h6 className="mb-0 fw-bold">Admin Users</h6>
          <form
            className="navbar-search"
            onSubmit={(e) => {
              e.preventDefault();
              fetchRows();
            }}
          >
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              placeholder="Search name, mobile, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </form>
        </div>
        {canManage && (
          <button
            type="button"
            className="btn btn-primary-600 d-inline-flex align-items-center gap-2"
            onClick={openCreate}
          >
            <Icon icon="solar:user-plus-bold" />
            Add Admin User
          </button>
        )}
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-24 text-secondary-light">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-24 text-secondary-light">
                    No admin users.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <div className="fw-semibold">{r.name}</div>
                      <div className="text-xs text-secondary-light">{r.uid}</div>
                    </td>
                    <td>{r.mobile}</td>
                    <td>{r.email || "—"}</td>
                    <td>{roleBadge(r.role, roleLabel(r.role))}</td>
                    <td>
                      {Number(r.status) === 1 ? (
                        <span className="badge bg-success-50 text-success-700">Active</span>
                      ) : (
                        <span className="badge bg-danger-50 text-danger-700">Inactive</span>
                      )}
                    </td>
                    <td className="text-xs">{r.created_at}</td>
                    <td className="text-center">
                      <div className="d-inline-flex gap-2">
                        {canManage && (r.role !== ROLES.SUPER_ADMIN || isSuperAdmin) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary-600"
                            onClick={() => openEdit(r)}
                          >
                            <Icon icon="solar:pen-bold" />
                          </button>
                        )}
                        {isSuperAdmin && r.role !== ROLES.SUPER_ADMIN && r.id !== me?.id && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(r)}
                          >
                            <Icon icon="solar:trash-bin-minimalistic-bold" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: 16,
          }}
          onClick={() => !saving && setShowForm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 24,
              width: "100%",
              maxWidth: 520,
              boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-20">
              <h6 className="mb-0 fw-bold">
                {form.id ? "Edit Admin User" : "Add Admin User"}
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowForm(false)}
                disabled={saving}
              >
                <Icon icon="radix-icons:cross-2" />
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Name *</label>
                  <input
                    className="form-control"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Mobile *</label>
                  <input
                    className="form-control"
                    required
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    maxLength={10}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Role *</label>
                  <select
                    className="form-select"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    disabled={form.role === ROLES.SUPER_ADMIN && !isSuperAdmin}
                  >
                    {assignableRoles.map((r) => (
                      <option key={r.role_key} value={r.role_key}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">
                    {form.id ? "New Password (leave blank to keep)" : "Password *"}
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    required={!form.id}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2 mt-24">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowForm(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary-600" disabled={saving}>
                  {saving ? "Saving..." : form.id ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersLayer;
